import React, { useState } from "react";
import parse from "html-react-parser";
import { Reference, Skill, SkillItem } from "../types";
import { useProfile } from "../context/ProfileContext";

type Props = {
  projects: Reference[];
  skills: Skill[];
};

function getSkillLevel(name: string, items: SkillItem[]): string | null {
  const found = items.find((si) => si.items.indexOf(name) !== -1);
  return found ? found.level : null;
}

function ProjectCard({
  project,
  allSkillItems,
}: {
  project: Reference;
  allSkillItems: SkillItem[];
}) {
  const [open, setOpen] = useState(false);
  const { isExperienceRelevant, isSkillRelevant, profile } = useProfile();

  const relevant = isExperienceRelevant(project.skills);
  const muted = profile !== "all" && !relevant;

  const dateRange = [
    project.startMonth || project.startYear
      ? `${project.startMonth || ""} ${project.startYear || ""}`.trim()
      : null,
    project.endMonth || project.endYear
      ? `${project.endMonth || ""} ${project.endYear || ""}`.trim()
      : null,
  ]
    .filter(Boolean)
    .join(" \u2192 ");

  const hasDetails =
    (project.descriptions && project.descriptions.length > 0) ||
    (project.subreferences && project.subreferences.length > 0);

  return (
    <div
      className={`glass-card p-5 transition-all duration-500 print-avoid-break ${
        muted ? "opacity-25" : "opacity-100"
      } ${hasDetails ? "cursor-pointer hover:border-neutral-300 dark:hover:border-white/10" : ""}`}
      onClick={() => hasDetails && setOpen(!open)}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-base text-neutral-900 dark:text-neutral-100">
            {project.name}
          </h3>
          <p className="text-sm text-neutral-400 dark:text-neutral-500 mt-0.5">
            {project.title}
          </p>
        </div>
        {hasDetails && (
          <button className="no-print shrink-0 w-6 h-6 rounded-lg flex items-center justify-center text-neutral-300 dark:text-neutral-600">
            <i
              className={`fa fa-chevron-down text-[9px] transition-transform duration-300 ${
                open ? "rotate-180" : ""
              }`}
            />
          </button>
        )}
      </div>

      {/* Date badge */}
      {dateRange && (
        <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-neutral-100 dark:bg-white/[0.06]">
          <i className="fa fa-calendar text-[9px] text-neutral-400 dark:text-neutral-500" />
          <span className="text-[11px] font-semibold text-neutral-600 dark:text-neutral-300 tabular-nums">
            {dateRange}
          </span>
        </div>
      )}

      {/* Skills */}
      {project.skills && project.skills.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1">
          {project.skills.map((skill) => {
            const level = getSkillLevel(skill, allSkillItems);
            const baseClass =
              level === "REGULAR"
                ? "exp-tag-regular"
                : level === "SOMETIMES"
                ? "exp-tag-outline"
                : "exp-tag-old";
            const highlightClass =
              profile !== "all" && isSkillRelevant(skill)
                ? "!bg-neutral-900 !text-white dark:!bg-white dark:!text-neutral-900 !border-transparent"
                : "";
            const mutedClass =
              profile !== "all" && !isSkillRelevant(skill) ? "opacity-30" : "";
            return (
              <span key={skill} className={`${baseClass} ${highlightClass} ${mutedClass}`}>
                {skill}
              </span>
            );
          })}
        </div>
      )}

      {/* Expandable details */}
      <div
        className={`exp-collapsible overflow-hidden transition-all duration-500 ${
          open ? "max-h-[3000px] opacity-100 mt-4" : "max-h-0 opacity-0"
        }`}
      >
        {project.descriptions &&
          project.descriptions.map((desc, i) =>
            desc.startsWith("@isHTML") ? (
              <div
                key={i}
                className="text-sm text-neutral-600 dark:text-neutral-300 mt-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1 [&_li]:text-sm"
              >
                {parse(desc.replace("@isHTML", ""))}
              </div>
            ) : (
              <p
                key={i}
                className="text-sm text-neutral-600 dark:text-neutral-300 leading-relaxed mt-2"
              >
                {desc}
              </p>
            )
          )}

        {/* Sub-entries as mini items */}
        {project.subreferences && project.subreferences.length > 0 && (
          <div className="mt-3 space-y-2 pl-3 border-l-2 border-neutral-100 dark:border-neutral-800">
            {project.subreferences.map((sub, i) => {
              const subDate = [
                sub.startMonth || sub.startYear
                  ? `${sub.startMonth || ""} ${sub.startYear || ""}`.trim()
                  : null,
                sub.endMonth || sub.endYear
                  ? `${sub.endMonth || ""} ${sub.endYear || ""}`.trim()
                  : null,
              ]
                .filter(Boolean)
                .join(" \u2192 ");

              return (
                <div key={i} className="py-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                      {sub.name}
                    </p>
                    {subDate && (
                      <span className="text-[10px] font-medium text-neutral-400 dark:text-neutral-500 bg-neutral-100 dark:bg-white/[0.06] px-1.5 py-0.5 rounded tabular-nums">
                        {subDate}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-neutral-400 dark:text-neutral-500">
                    {sub.title}
                  </p>
                  {sub.descriptions &&
                    sub.descriptions.map((d, j) => (
                      <p
                        key={j}
                        className="text-xs text-neutral-500 dark:text-neutral-400 mt-1 leading-relaxed"
                      >
                        {d}
                      </p>
                    ))}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Projects({ projects, skills }: Props) {
  const allSkillItems = skills.flatMap((s) => s.items || []);

  return (
    <section>
      <h2 className="section-label mb-6">Projets</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {projects.map((project, i) => (
          <ProjectCard
            key={i}
            project={project}
            allSkillItems={allSkillItems}
          />
        ))}
      </div>
    </section>
  );
}
